// frontend/src/components/crm/WorkflowNode.js
import React, { useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import {
    CogIcon,
    TrashIcon,
    PencilIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const WorkflowNode = ({ data, selected, id, type: nodeType }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [nodeData, setNodeData] = useState(data);
    const { deleteElements } = useReactFlow();
    
    const handleDelete = (e) => {
        e.stopPropagation();
        if (deleteElements && id) {
            deleteElements({ nodes: [{ id }] });
        }
    };

    const handleSave = () => {
        // Update node data
        setIsEditing(false);
    };

    // Determine node type from nodeType prop or fallback to data.type
    const type = nodeType || data?.type || 'action';
    
    const getNodeIcon = (nodeType) => {
        switch (nodeType) {
            case 'trigger':
                return <div className="w-3 h-3 bg-green-500 rounded-full"></div>;
            case 'action':
                return <div className="w-3 h-3 bg-blue-500 rounded-full"></div>;
            case 'condition':
                return <div className="w-3 h-3 bg-orange-500 rounded-full"></div>;
            default:
                return <div className="w-3 h-3 bg-muted0 rounded-full"></div>;
        }
    };

    const getNodeColor = (nodeType) => {
        switch (nodeType) {
            case 'trigger':
                return 'border-green-500 bg-green-50';
            case 'action':
                return 'border-blue-500 bg-blue-50';
            case 'condition':
                return 'border-orange-500 bg-orange-50';
            default:
                return 'border-input bg-muted';
        }
    };

    return (
        <div className={`relative group ${selected ? 'ring-2 ring-blue-500' : ''}`}>
            <Handle
                type="target"
                position={Position.Top}
                className="w-3 h-3 bg-gray-400"
            />
            
            <div className={`px-4 py-3 shadow-md rounded-lg border-2 ${getNodeColor(type)} min-w-[200px]`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {getNodeIcon(type)}
                        <div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={nodeData.label}
                                    onChange={(e) => setNodeData({ ...nodeData, label: e.target.value })}
                                    className="text-sm font-bold bg-transparent border-none outline-none"
                                    autoFocus
                                />
                            ) : (
                                <div className="text-sm font-bold">{nodeData.label}</div>
                            )}
                        </div>
                    </div>
                    
                    {selected && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => setIsEditing(!isEditing)}
                                className="p-1 hover:bg-muted rounded"
                            >
                                <PencilIcon className="h-3 w-3" />
                            </button>
                            <button
                                onClick={handleDelete}
                                className="p-1 hover:bg-red-200 rounded"
                                title="Delete node"
                            >
                                <TrashIcon className="h-3 w-3 text-red-600" />
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="text-xs text-muted-foreground mt-1">
                    {isEditing ? (
                        <textarea
                            value={nodeData.description}
                            onChange={(e) => setNodeData({ ...nodeData, description: e.target.value })}
                            className="w-full text-xs bg-transparent border-none outline-none resize-none"
                            rows={2}
                        />
                    ) : (
                        nodeData.description
                    )}
                </div>

                {isEditing && (
                    <div className="mt-2 flex gap-2">
                        <button
                            onClick={handleSave}
                            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setIsEditing(false)}
                            className="px-2 py-1 bg-gray-300 text-foreground text-xs rounded hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* Node Configuration Summary */}
                {type === 'action' && !isEditing && (() => {
                    const actionType = nodeData.type || data?.type;
                    const configItems = [];
                    
                    // Email action
                    if (actionType === 'send_email' && nodeData.email) {
                        if (nodeData.email.templateName) {
                            configItems.push(`Template: ${nodeData.email.templateName}`);
                        } else if (nodeData.email.templateId) {
                            configItems.push(`Template ID: ${nodeData.email.templateId.substring(0, 8)}...`);
                        }
                        if (nodeData.email.subject) {
                            configItems.push(`Subject: ${nodeData.email.subject.substring(0, 25)}${nodeData.email.subject.length > 25 ? '...' : ''}`);
                        }
                        if (nodeData.email.recipientType) {
                            const recipientLabel = nodeData.email.recipientType === 'custom' && nodeData.email.customEmail 
                                ? nodeData.email.customEmail 
                                : nodeData.email.recipientType;
                            configItems.push(`To: ${recipientLabel}`);
                        } else if (nodeData.email.recipients && Array.isArray(nodeData.email.recipients) && nodeData.email.recipients.length > 0) {
                            configItems.push(`Recipients: ${nodeData.email.recipients.length} email(s)`);
                        }
                    }
                    // Task action
                    else if (actionType === 'create_task' && nodeData.task) {
                        if (nodeData.task.title) {
                            configItems.push(`Title: ${nodeData.task.title}`);
                        }
                        if (nodeData.task.category) {
                            configItems.push(`Category: ${nodeData.task.category}`);
                        }
                        if (nodeData.task.priority) {
                            configItems.push(`Priority: ${nodeData.task.priority}`);
                        }
                    }
                    // Survey action
                    else if (actionType === 'send_survey' && nodeData.survey) {
                        if (nodeData.survey.formName) {
                            configItems.push(`Form: ${nodeData.survey.formName}`);
                        } else if (nodeData.survey.formId) {
                            configItems.push(`Form ID: ${nodeData.survey.formId.substring(0, 8)}...`);
                        }
                        if (nodeData.survey.recipientType) {
                            const recipientLabel = nodeData.survey.recipientType === 'custom' && nodeData.survey.recipientEmail
                                ? nodeData.survey.recipientEmail
                                : nodeData.survey.recipientType;
                            configItems.push(`To: ${recipientLabel}`);
                        }
                    }
                    // Status update action
                    else if (actionType === 'update_status' && nodeData.statusUpdate) {
                        if (nodeData.statusUpdate.entity) {
                            configItems.push(`Entity: ${nodeData.statusUpdate.entity}`);
                        }
                        if (nodeData.statusUpdate.status) {
                            configItems.push(`Status: ${nodeData.statusUpdate.status}`);
                        }
                    }
                    // Assignment action
                    else if (actionType === 'assign_user' && nodeData.assignment) {
                        if (nodeData.assignment.entity) {
                            configItems.push(`Entity: ${nodeData.assignment.entity}`);
                        }
                        if (nodeData.assignment.assignedTo || nodeData.assignment.userId) {
                            configItems.push(`Assigned to: ${nodeData.assignment.assignedTo || nodeData.assignment.userId}`);
                        }
                    }
                    // Delay action
                    else if (actionType === 'delay' && nodeData.delay) {
                        if (nodeData.delay.amount) {
                            configItems.push(`Delay: ${nodeData.delay.amount} ${nodeData.delay.unit || 'minutes'}`);
                        }
                    }
                    // Webhook action
                    else if (actionType === 'webhook_call' && nodeData.webhook) {
                        if (nodeData.webhook.url) {
                            configItems.push(`URL: ${nodeData.webhook.url.substring(0, 30)}${nodeData.webhook.url.length > 30 ? '...' : ''}`);
                        }
                        if (nodeData.webhook.method) {
                            configItems.push(`Method: ${nodeData.webhook.method}`);
                        }
                    }
                    // Notification action
                    else if (actionType === 'send_notification' && nodeData.notification) {
                        if (nodeData.notification.title || nodeData.notification.message) {
                            configItems.push(`Message: ${(nodeData.notification.title || nodeData.notification.message || '').substring(0, 30)}${(nodeData.notification.title || nodeData.notification.message || '').length > 30 ? '...' : ''}`);
                        }
                    }
                    // Lead actions
                    else if ((actionType === 'create_lead' || actionType === 'update_lead') && nodeData.lead) {
                        if (nodeData.lead.firstName || nodeData.lead.lastName) {
                            configItems.push(`Name: ${(nodeData.lead.firstName || '')} ${(nodeData.lead.lastName || '')}`.trim());
                        }
                        if (nodeData.lead.email) {
                            configItems.push(`Email: ${nodeData.lead.email}`);
                        }
                    }
                    // Opportunity actions
                    else if ((actionType === 'create_opportunity' || actionType === 'update_opportunity') && nodeData.opportunity) {
                        if (nodeData.opportunity.name) {
                            configItems.push(`Name: ${nodeData.opportunity.name}`);
                        }
                        if (nodeData.opportunity.amount) {
                            configItems.push(`Amount: ${nodeData.opportunity.amount}`);
                        }
                    }
                    // Condition action
                    else if ((actionType === 'condition_check' || type === 'condition') && nodeData.condition) {
                        if (nodeData.condition.field) {
                            configItems.push(`Field: ${nodeData.condition.field}`);
                        }
                        if (nodeData.condition.operator) {
                            configItems.push(`Operator: ${nodeData.condition.operator}`);
                        }
                        if (nodeData.condition.value !== undefined && nodeData.condition.value !== '') {
                            configItems.push(`Value: ${nodeData.condition.value}`);
                        }
                    }
                    
                    if (configItems.length === 0) {
                        return null;
                    }
                    
                    return (
                        <div className="mt-2 p-2 bg-card rounded border text-xs">
                            <div className="font-medium mb-1 text-muted-foreground">Configuration:</div>
                            <div className="space-y-0.5">
                                {configItems.map((item, idx) => (
                                    <div key={idx} className="text-muted-foreground truncate">
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

            </div>

            {/* Condition nodes have two output handles (true/false) */}
            {type === 'condition' ? (
                <div className="relative pb-6">
                    <Handle
                        type="source"
                        position={Position.Bottom}
                        id="true"
                        style={{ 
                            left: '30%', 
                            background: '#10b981',
                            border: '2px solid #059669',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%'
                        }}
                        className="!bg-green-500 hover:!bg-green-600 hover:!scale-110 transition-all cursor-crosshair"
                    />
                    <div 
                        className="absolute -bottom-4 left-[30%] transform -translate-x-1/2 text-[10px] text-green-600 dark:text-green-400 font-bold whitespace-nowrap pointer-events-none"
                    >
                        True
                    </div>
                    <Handle
                        type="source"
                        position={Position.Bottom}
                        id="false"
                        style={{ 
                            left: '70%', 
                            background: '#ef4444',
                            border: '2px solid #dc2626',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%'
                        }}
                        className="!bg-red-500 hover:!bg-red-600 hover:!scale-110 transition-all cursor-crosshair"
                    />
                    <div 
                        className="absolute -bottom-4 left-[70%] transform -translate-x-1/2 text-[10px] text-red-600 dark:text-red-400 font-bold whitespace-nowrap pointer-events-none"
                    >
                        False
                    </div>
                </div>
            ) : (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    className="w-3 h-3 bg-gray-400"
                />
            )}
        </div>
    );
};

export default WorkflowNode;
